import { useEffect } from 'react'

import {
  useAuth,
  useDonationSummaryFiscalPeriod,
  useHasWalletRewards,
  useLogger
} from '@percent/workplace-giving/common/hooks'
import { Money } from '@percent/utility'
import {
  getAccountConfigFromAuthState,
  getPartnerFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'

export type TitleTooltipKey =
  | 'walletRewardsMatching'
  | 'walletMatching'
  | 'rewardsMatching'
  | 'matching'
  | 'walletRewards'
  | 'wallet'
  | 'rewards'

/**
 * The tooltip lists only the giving sources this partner actually has, so each combination gets its
 * own copy. When none apply the total is just the employee's own giving — which the "Donated" label
 * already says — so there is no tooltip at all.
 */
const titleTooltipKeyFor = ({
  hasWallet,
  hasRewards,
  hasMatching
}: {
  hasWallet: boolean
  hasRewards: boolean
  hasMatching: boolean
}): TitleTooltipKey | undefined => {
  const sources = [hasWallet && 'wallet', hasRewards && 'rewards', hasMatching && 'matching'].filter(Boolean).join('+')

  const keysBySources: Record<string, TitleTooltipKey | undefined> = {
    'wallet+rewards+matching': 'walletRewardsMatching',
    'wallet+matching': 'walletMatching',
    'rewards+matching': 'rewardsMatching',
    matching: 'matching',
    'wallet+rewards': 'walletRewards',
    wallet: 'wallet',
    rewards: 'rewards'
  }

  return keysBySources[sources]
}

export type TotalDonatedCardReturn = Readonly<{
  isLoading: boolean
  totalDonated: Money
  partnerName: string
  titleTooltipKey: TitleTooltipKey | undefined
}>

export const useTotalDonatedCard = (): TotalDonatedCardReturn => {
  const { state: authState } = useAuth()
  const { logError } = useLogger()
  const has = usePermission()
  const accountConfig = getAccountConfigFromAuthState(authState)
  const partner = getPartnerFromAuthState(authState)

  const { data, isPending, error } = useDonationSummaryFiscalPeriod()

  const currencyCode = accountConfig?.currency ?? partner?.currencyCode

  // Wallet rewards resolve asynchronously (scope + rules-summary endpoint), so this is false until that settles.
  const hasRewards = useHasWalletRewards()

  const titleTooltipKey = titleTooltipKeyFor({
    hasWallet: has(scope('donation_wallet')),
    hasRewards,
    hasMatching: has(scope('donation_matching'))
  })

  useEffect(() => {
    if (error) {
      logError(error)
    }
  }, [error, logError])

  return {
    isLoading: isPending,
    totalDonated: data?.totalDonated ?? { amount: 0, currency: currencyCode ?? 'USD' },
    partnerName: partner?.name ?? '',
    titleTooltipKey
  }
}
